import * as GQL from "graphql";
import { getNamedTypeNode, isListTypeNode, isNonNullType } from "./schemaUtils";
export const argumentsByField = {};
// We keep track of all enums to properly identify how to build
// the query string
export const enums = [];
const nativeTypes = {
    String: "string",
    UUID4: "string",
    ID: "string",
    DateTime: "string",
    NaiveDateTime: "string",
    Base64: "string",
    Boolean: "boolean",
    Int: "number",
    Float: "number",
};
export function createValueType(value) {
    return nativeTypes[value] || value;
}
export function createInputValues(field, args) {
    let string = "{\n";
    // We might have the same name, but variables are very likely to be different and if same very likely to also be expressed the same
    argumentsByField[field] = argumentsByField[field] || {};
    for (const arg of args) {
        const isNonNull = isNonNullType(arg.type);
        const isList = isListTypeNode(arg.type);
        argumentsByField[field][arg.name.value] = {
            isNonNull,
            type: getNamedTypeNode(arg.type).name.value,
            isList,
        };
        string += `    ${arg.name.value}${isNonNull ? "" : "?"}: ${createValueType(getNamedTypeNode(arg.type).name.value)}${isList ? "[]" : ""}`;
        if (arg !== args[args.length - 1]) {
            string += "\n";
        }
    }
    return string + "\n  }";
}
export function createObjectTypeKey(field) {
    const isList = isListTypeNode(field.type);
    const isNonNull = isNonNullType(field.type);
    const key = field.name.value;
    if (isList && field.arguments && field.arguments.length) {
        return `${key}: ListQuery<${createInputValues(field.name.value, field.arguments)}, ${createValueType(getNamedTypeNode(field.type).name.value)}>${isNonNull ? "" : " | null"}`;
    }
    const typedNameNode = getNamedTypeNode(field.type);
    // Not sure if this can be null?
    if (isList && !(typedNameNode.name.value in nativeTypes)) {
        return `${key}: ListField<${createValueType(typedNameNode.name.value)}>`;
    }
    if (field.arguments && field.arguments.length) {
        return `${key}: FieldQuery<${createInputValues(field.name.value, field.arguments)}, ${createValueType(typedNameNode.name.value)}>${isNonNull ? "" : " | null"}`;
    }
    return `${key}: ${createValueType(typedNameNode.name.value)}${isList ? "[]" : ""}${isNonNull ? "" : " | null"}`;
}
export function createObjectType(definition) {
    if (!definition.fields) {
        return `export type ${definition.name.value} {}`;
    }
    let string = `export type ${definition.name.value} = {\n`;
    string += [`  __typename: "${definition.name.value}"`]
        .concat(definition.fields.map((field) => `  ${createObjectTypeKey(field)}`))
        .join("\n");
    return string + "\n}\n";
}
export function createInputObjectType(definition) {
    if (!definition.fields) {
        return `export type ${definition.name.value} = {}`;
    }
    let string = `export type ${definition.name.value} = {\n`;
    string += definition.fields
        .map((field) => {
        const isNonNull = isNonNullType(field.type);
        return `  ${field.name.value}${isNonNull ? "" : "?"}: ${createValueType(getNamedTypeNode(field.type).name.value)};`;
    })
        .join("\n");
    return string + "\n}\n";
}
export function createEnum(definition) {
    if (!definition.values) {
        return `export const ${definition.name.value} = {}`;
    }
    const values = definition.values;
    let string = `export const ${definition.name.value} = {\n`;
    string += values
        .map((value) => `  ${value.name.value}: enums[${enums.indexOf(value.name.value)}]`)
        .join(",\n");
    return string + "\n}\n";
}
export function createEnumType(definition) {
    if (!definition.values) {
        return `export enum ${definition.name.value} {}`;
    }
    const values = definition.values;
    let string = `export enum ${definition.name.value} {\n`;
    values.forEach((value) => {
        if (!enums.includes(value.name.value)) {
            enums.push(value.name.value);
        }
        // We reference the string as opposed to adding it again. The "enums"
        // will be made a variable in the context of this code
        string += `  ${value.name.value} = "${value.name.value}"`;
        if (value !== values[values.length - 1]) {
            string += ",\n";
        }
    });
    return string + "\n}\n";
}
export function createUnionType(definition) {
    const types = definition.types || [];
    return `
export type ${definition.name.value} = ${types
        .map((type) => type.name.value)
        .join(" |")}\n`;
}
export function createEnumTypes(definitions) {
    let enums = "";
    for (const definition of definitions) {
        if (definition.kind === GQL.Kind.ENUM_TYPE_DEFINITION) {
            enums += createEnum(definition);
            continue;
        }
    }
    return enums;
}
export function createTypes(definitions) {
    let types = "";
    const scalarTypeDefinitions = [];
    const objectTypes = new Set();
    for (const definition of definitions) {
        if (definition.kind === GQL.Kind.SCALAR_TYPE_DEFINITION) {
            scalarTypeDefinitions.push(definition);
            continue;
        }
        if (definition.kind === GQL.Kind.OBJECT_TYPE_DEFINITION) {
            types += createObjectType(definition);
            objectTypes.add(definition.name.value);
            continue;
        }
        if (definition.kind === GQL.Kind.INPUT_OBJECT_TYPE_DEFINITION) {
            types += createInputObjectType(definition);
            continue;
        }
        if (definition.kind === GQL.Kind.ENUM_TYPE_DEFINITION) {
            types += createEnumType(definition);
            continue;
        }
        if (definition.kind === GQL.Kind.UNION_TYPE_DEFINITION) {
            types += createUnionType(definition);
            continue;
        }
    }
    types += "export type ObjectTypes = {\n";
    objectTypes.forEach((type) => {
        types += `  ${type}: ${type};\n`;
    });
    types += "}";
    return types;
}
export function createFragments(definitions) {
    let fragments = "";
    const objectTypes = new Set();
    for (const definition of definitions) {
        if (definition.kind === GQL.Kind.OBJECT_TYPE_DEFINITION) {
            objectTypes.add(definition.name.value);
            continue;
        }
    }
    fragments += "export const fragments: {\n";
    objectTypes.forEach((type) => {
        fragments += `  ${type}<T extends ResolveQueryDefinitions<${type}>>(fragment: T): T & FragmentDataType<${type}>,\n`;
    });
    fragments += "}";
    return fragments;
}
export const InlineFragmentSymbol = Symbol("INLINE_FRAGMENT");
export function createInlineFragments(definitions) {
    let inlineFragments = "";
    const unionTypes = new Set();
    for (const definition of definitions) {
        if (definition.kind === GQL.Kind.UNION_TYPE_DEFINITION) {
            unionTypes.add({
                name: definition.name.value,
                types: (definition.types || []).map((type) => type.name.value),
            });
            continue;
        }
    }
    inlineFragments += "export const inlineFragments: {\n";
    unionTypes.forEach(({ name, types }) => {
        let unions = "{\n";
        for (const type of types) {
            unions += `  ${type}?: ResolveQueryDefinitions<${type}>\n`;
        }
        unions += "}\n";
        inlineFragments += `  ${name}<T extends ${unions}>(unions: T): T & { [InlineFragmentSymbol]: true },\n`;
    });
    inlineFragments += "}";
    return inlineFragments;
}
//# sourceMappingURL=typeUtils.js.map